import React, { useState, useRef } from 'react';
import { Upload, Loader2, ArrowLeft, Check, AlertTriangle, X, FileSpreadsheet, Download, Sparkles } from 'lucide-react';
import { productAPI } from '../../services/api';

const BulkImportWizard = ({ onBack, onComplete }) => {
  const fileRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload, preview, importing, done
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState('all'); // all, warnings, clean
  const [error, setError] = useState('');
  const [lookingUpHSN, setLookingUpHSN] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');

    try {
      setUploading(true);
      const data = await productAPI.bulkImportPreview(file);
      setPreview(data);
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to parse file');
    } finally { setUploading(false); }
  };

  const handleImport = async () => {
    if (!preview) return;
    const itemsToImport = preview.items.filter(i => !i.is_duplicate);

    try {
      setImporting(true);
      setStep('importing');
      const res = await productAPI.bulkImportConfirm({ items: itemsToImport });
      setResult(res);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed');
      setStep('preview');
    } finally { setImporting(false); }
  };

  const handleHSNLookup = async () => {
    if (!preview) return;
    const missingHSN = preview.items.filter(i => !i.hsn_code && !i.is_duplicate);
    if (missingHSN.length === 0) { alert('All items already have HSN codes'); return; }

    try {
      setLookingUpHSN(true);
      // Process in batches of 20
      const batchSize = 20;
      const updatedItems = [...preview.items];

      for (let i = 0; i < missingHSN.length; i += batchSize) {
        const batch = missingHSN.slice(i, i + batchSize);
        const descriptions = batch.map(b => b.name);
        const res = await productAPI.hsnLookup(descriptions);

        if (res.results) {
          res.results.forEach((r, idx) => {
            if (r.hsn_code) {
              const itemIdx = updatedItems.findIndex(it => it.sku === batch[idx]?.sku);
              if (itemIdx >= 0) {
                updatedItems[itemIdx] = {
                  ...updatedItems[itemIdx],
                  hsn_code: r.hsn_code,
                  hsn_ai_suggested: true,
                  hsn_confidence: r.confidence,
                  hsn_reason: r.reason,
                  warnings: (updatedItems[itemIdx].warnings || []).filter(w => w !== 'Missing HSN code'),
                };
              }
            }
          });
        }
      }

      const newMissingHSN = updatedItems.filter(i => !i.hsn_code && !i.is_duplicate).length;
      setPreview({ ...preview, items: updatedItems, missing_hsn: newMissingHSN });
    } catch (err) {
      setError('HSN lookup failed: ' + (err.response?.data?.detail || err.message));
    } finally { setLookingUpHSN(false); }
  };

  const filteredItems = preview?.items?.filter(item => {
    if (filter === 'warnings') return item.warnings?.length > 0;
    if (filter === 'clean') return !item.warnings?.length && !item.is_duplicate;
    return true;
  }) || [];

  // ===== UPLOAD STEP =====
  if (step === 'upload') {
    return (
      <div data-testid="bulk-import-wizard">
        <div className="flex items-center gap-3 mb-6">
          <button data-testid="import-back-btn" onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100"><ArrowLeft className="w-4 h-4 text-gray-500" /></button>
          <div>
            <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Bulk Product Import</h2>
            <p className="text-[11px] text-gray-500">Upload an Excel file to import products into the system</p>
          </div>
        </div>

        <div className="max-w-lg mx-auto mt-10">
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-all group"
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
                <p className="text-sm text-gray-600">Parsing your Excel file...</p>
              </div>
            ) : (
              <>
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform" style={{ backgroundColor: '#24549c15' }}>
                  <FileSpreadsheet className="w-7 h-7" style={{ color: '#24549c' }} />
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">Drop your Excel file here</p>
                <p className="text-xs text-gray-500 mb-3">or click to browse (.xlsx, .xlsm)</p>
                <div className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>
                  <Upload className="w-3.5 h-3.5" /> Select File
                </div>
              </>
            )}
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls" onChange={handleFileSelect} className="hidden" />

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <p className="text-xs font-bold text-gray-900 mb-2">Expected Format</p>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              Your Excel should have a header row with columns like: <strong>SKU Code</strong>, <strong>Item Description</strong>, <strong>UOM</strong>, <strong>HSN</strong>, <strong>GST</strong>, <strong>MIN Stock</strong>, <strong>MAX Stock</strong>, <strong>Selling Price</strong>, <strong>Purchase Price</strong>.
            </p>
            <p className="text-[11px] text-gray-500 mt-2">Branch/godown columns (e.g. "Ranchi HQ", "Siliguri Godown") will be auto-detected and imported as opening stock per location.</p>
          </div>
        </div>
      </div>
    );
  }

  // ===== PREVIEW STEP =====
  if (step === 'preview') {
    return (
      <div data-testid="bulk-import-preview">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => { setStep('upload'); setPreview(null); setError(''); }} className="p-1.5 rounded-lg hover:bg-gray-100"><ArrowLeft className="w-4 h-4 text-gray-500" /></button>
            <div>
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Import Preview</h2>
              <p className="text-[11px] text-gray-500">Review and confirm before importing</p>
            </div>
          </div>
          <button data-testid="confirm-import-btn" onClick={handleImport} disabled={preview.importable === 0}
            className="flex items-center gap-2 px-5 py-2.5 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50 transition-all" style={{ backgroundColor: '#24549c' }}>
            <Download className="w-3.5 h-3.5" /> Import {preview.importable} Products
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-4">
          {[
            { label: 'Total Rows', value: preview.total_rows, color: '#24549c' },
            { label: 'Ready to Import', value: preview.importable, color: '#16a34a' },
            { label: 'Duplicates (Skip)', value: preview.duplicates, color: '#f59e0b' },
            { label: 'Missing HSN', value: preview.missing_hsn, color: '#ef4444' },
            { label: 'Uncategorized', value: preview.uncategorized, color: '#8b5cf6' },
            { label: 'Stock Entries', value: preview.total_stock_entries || 0, color: '#0891b2' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-3">
              <p className="text-[10px] text-gray-500 font-medium">{s.label}</p>
              <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Detected branches info */}
        {preview.detected_branches?.length > 0 && (
          <div className="mb-4 p-3 bg-cyan-50 border border-cyan-200 rounded-xl">
            <p className="text-xs font-semibold text-cyan-700 mb-1">Branch Stock Columns Detected ({preview.detected_branches.length})</p>
            <div className="flex flex-wrap gap-1.5">
              {preview.detected_branches.map((b, i) => (
                <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${preview.unmatched_branches?.includes(b) ? 'bg-amber-100 text-amber-700' : 'bg-cyan-100 text-cyan-700'}`}>
                  {b} {preview.unmatched_branches?.includes(b) ? '(new branch)' : ''}
                </span>
              ))}
            </div>
            {preview.unmatched_branches?.length > 0 && (
              <p className="text-[10px] text-amber-600 mt-1">New branches will be created automatically during import.</p>
            )}
          </div>
        )}

        {/* Auto HSN Lookup */}
        {preview.missing_hsn > 0 && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <p className="text-xs text-amber-700"><strong>{preview.missing_hsn}</strong> items are missing HSN codes. Use AI to auto-suggest them.</p>
            </div>
            <button data-testid="auto-hsn-btn" onClick={handleHSNLookup} disabled={lookingUpHSN}
              className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
              {lookingUpHSN ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {lookingUpHSN ? 'Looking up HSN codes...' : 'Auto-Fill HSN Codes'}
            </button>
          </div>
        )}

        {error && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-1 mb-3 bg-gray-100 p-1 rounded-lg w-fit">
          {[
            { id: 'all', label: `All (${preview.items.length})` },
            { id: 'warnings', label: `With Issues (${preview.items.filter(i => i.warnings?.length > 0).length})` },
            { id: 'clean', label: `Clean (${preview.items.filter(i => !i.warnings?.length && !i.is_duplicate).length})` },
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-[11px] font-semibold rounded-md transition-all ${filter === f.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto max-h-[450px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 z-10">
                <tr className="text-[10px] font-bold text-gray-500 uppercase" style={{ backgroundColor: '#f8f9fb' }}>
                  {['#', 'SKU', 'Name', 'Category', 'Group', 'UOM', 'HSN', 'GST', 'Type', 'Status'].map(h => (
                    <th key={h} className="text-left px-3 py-2.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredItems.slice(0, 200).map((item, i) => (
                  <tr key={i} className={`border-b border-gray-50 text-xs hover:bg-gray-50 ${item.is_duplicate ? 'opacity-50 bg-yellow-50/30' : ''}`}>
                    <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                    <td className="px-3 py-2 font-mono font-semibold text-gray-700">{item.sku}</td>
                    <td className="px-3 py-2 text-gray-900 max-w-[200px] truncate">{item.name}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${item.category === 'Uncategorized' ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-600'}`}>
                        {item.category}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-500">{item.sub_category || '-'}</td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full">{item.unit}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-gray-500">
                      {item.hsn_code ? (
                        <span className={item.hsn_ai_suggested ? 'flex items-center gap-1' : ''}>
                          {item.hsn_code}
                          {item.hsn_ai_suggested && (
                            <span className={`text-[8px] px-1 py-0.5 rounded-full font-bold ${item.hsn_confidence === 'high' ? 'bg-green-100 text-green-600' : item.hsn_confidence === 'medium' ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'}`} title={item.hsn_reason}>
                              AI
                            </span>
                          )}
                        </span>
                      ) : <span className="text-red-400 text-[10px]">Missing</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{item.gst_rate}%</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${item.transaction_type === 'Both' ? 'bg-green-50 text-green-700' : item.transaction_type === 'Buy' ? 'bg-orange-50 text-orange-700' : 'bg-blue-50 text-blue-700'}`}>
                        {item.transaction_type}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {item.is_duplicate ? (
                        <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full font-medium">Duplicate</span>
                      ) : item.warnings?.length > 0 ? (
                        <span className="text-[10px] bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded-full font-medium" title={item.warnings.join(', ')}>
                          {item.warnings.length} issue{item.warnings.length > 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="text-[10px] bg-green-50 text-green-600 px-1.5 py-0.5 rounded-full font-medium">Ready</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredItems.length > 200 && (
            <div className="p-3 text-center text-xs text-gray-500 bg-gray-50">
              Showing 200 of {filteredItems.length} items
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===== IMPORTING / DONE STEP =====
  if (step === 'importing') {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-10 h-10 animate-spin mb-4" style={{ color: '#24549c' }} />
        <p className="text-sm font-semibold text-gray-900">Importing products...</p>
        <p className="text-xs text-gray-500 mt-1">This may take a moment for large files</p>
      </div>
    );
  }

  if (step === 'done' && result) {
    return (
      <div data-testid="import-result" className="max-w-md mx-auto mt-16 text-center">
        <div className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: '#24549c15' }}>
          <Check className="w-8 h-8" style={{ color: '#24549c' }} />
        </div>
        <h2 className="text-lg font-bold text-gray-900 mb-1">Import Complete</h2>
        <p className="text-xs text-gray-500 mb-6">Your products have been imported into the system</p>

        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-green-50 rounded-xl p-4">
            <p className="text-2xl font-bold text-green-600">{result.imported}</p>
            <p className="text-[10px] text-green-600 font-medium">Imported</p>
          </div>
          <div className="bg-cyan-50 rounded-xl p-4">
            <p className="text-2xl font-bold text-cyan-600">{result.stock_created || 0}</p>
            <p className="text-[10px] text-cyan-600 font-medium">Stock Entries</p>
          </div>
          <div className="bg-yellow-50 rounded-xl p-4">
            <p className="text-2xl font-bold text-yellow-600">{result.skipped}</p>
            <p className="text-[10px] text-yellow-600 font-medium">Skipped</p>
          </div>
          <div className="bg-red-50 rounded-xl p-4">
            <p className="text-2xl font-bold text-red-600">{result.errors}</p>
            <p className="text-[10px] text-red-600 font-medium">Errors</p>
          </div>
        </div>

        {result.error_details?.length > 0 && (
          <div className="text-left bg-red-50 rounded-lg p-3 mb-4">
            <p className="text-xs font-semibold text-red-700 mb-1">Error Details:</p>
            {result.error_details.map((e, i) => (
              <p key={i} className="text-[10px] text-red-600">{e}</p>
            ))}
          </div>
        )}

        <button onClick={onComplete}
          className="inline-flex items-center gap-2 px-6 py-2.5 text-xs font-semibold text-white rounded-lg hover:shadow-lg" style={{ backgroundColor: '#24549c' }}>
          <Check className="w-3.5 h-3.5" /> Go to Product Master
        </button>
      </div>
    );
  }

  return null;
};

export default BulkImportWizard;
